/**
 * PHARE : le disque est une lentille, avec un point de lumiere au centre, et un faisceau a la
 * couleur du client balaie lentement la page autour.
 *
 * L'IDEE, EN UN MOT : une presence qui veille. Le faisceau deborde tres largement du disque —
 * 260 px de diametre pour un lanceur de 64 — donc rien de tout cela ne peut vivre dans le rond de
 * la bulle, qui rogne ce qui depasse. Il est masque en radial pour s'eteindre avant les bords, et
 * il ne prend jamais le clic.
 *
 * AU TEMPS FORT, la lampe eclate une fois. A L'OUVERTURE, le faisceau s'eteint et la lampe se
 * retire pour laisser place a une croix de lumiere, halo compris : c'est le meme phare.
 *
 * CE FICHIER N'IMPORTE RIEN, PAR CONTRAT, et sa feuille est un gabarit de chaine : deux accents
 * graves dans le fichier, pas un de plus.
 */
export default {
  id: 'phare',

  css: `
/* LE FAISCEAU NE PREND PAS LE CLIC : il couvre 260 px autour du lanceur, soit un piege a clic
   invisible sur la page du client si on le laissait recevoir le pointeur. */
.woot-lanceur[data-look="phare"] .woot-phare__faisceau {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 260px;
  height: 260px;
  margin: -130px;
  border-radius: 50%;
  background: conic-gradient(from 0deg at 50% 50%, transparent 0 86%, var(--client) 92%, var(--client) 94%, transparent 100%);
  background: conic-gradient(from 0deg at 50% 50%, transparent 0 86%, color-mix(in srgb, var(--client) 34%, transparent) 92%, color-mix(in srgb, var(--client) 48%, transparent) 94%, transparent 100%);
  -webkit-mask: radial-gradient(circle, #000 8%, rgba(0, 0, 0, .55) 30%, transparent 60%);
  mask: radial-gradient(circle, #000 8%, rgba(0, 0, 0, .55) 30%, transparent 60%);
  transition: opacity .4s ease;
}

.woot-lanceur[data-look="phare"][data-etat="repos"] .woot-phare__faisceau,
.woot-lanceur[data-look="phare"][data-etat="attention"] .woot-phare__faisceau {
  animation: woot-phare-balaye 9s linear infinite;
}

@keyframes woot-phare-balaye { to { transform: rotate(360deg); } }

.woot-lanceur[data-look="phare"] .woot-phare__disque {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: var(--client);
  background: radial-gradient(circle at 50% 50%, color-mix(in srgb, var(--client) 30%, white) 0%, color-mix(in srgb, var(--client) 72%, white) 16%, var(--client) 44%, color-mix(in srgb, var(--client) 70%, black) 100%);
  box-shadow: 0 12px 30px rgba(16, 19, 23, .28);
  box-shadow: 0 12px 30px color-mix(in srgb, var(--client) 38%, transparent);
  transition: filter .3s ease;
}

/* La lentille : un liseret clair en haut, une ombre portee en bas. Un verre, pas un aplat. */
.woot-lanceur[data-look="phare"] .woot-phare__lentille {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, .5), inset 0 -8px 16px rgba(0, 0, 0, .2), inset 0 0 0 1px rgba(255, 255, 255, .16);
}

.woot-lanceur[data-look="phare"] .woot-phare__lampe {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 10px;
  height: 10px;
  margin: -5px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 0 14px 6px rgba(255, 255, 255, .6), 0 0 36px 16px rgba(255, 255, 255, .3);
  box-shadow: 0 0 14px 6px rgba(255, 255, 255, .6), 0 0 36px 16px color-mix(in srgb, var(--client) 35%, white);
  transition: transform .4s ease;
}

.woot-lanceur[data-look="phare"][data-etat="repos"] .woot-phare__lampe { animation: woot-phare-pulse 9s linear infinite; }
@keyframes woot-phare-pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.25); } }

/* LE TEMPS FORT : la lampe eclate, une fois, sans cesser de respirer. */
.woot-lanceur[data-look="phare"][data-etat="attention"] .woot-phare__lampe {
  animation: woot-phare-pulse 9s linear infinite, woot-phare-eclat 1.6s ease-out;
}

/* LE REPLI VAUT AUSSI DANS UNE KEYFRAME, et c'est meme la qu'il compte le plus : une etape dont
   la seule declaration est jetee a l'analyse devient une etape vide, donc l'eclat n'anime plus
   rien du tout et le temps fort de Phare disparait au lieu de degrader. */
@keyframes woot-phare-eclat {
  0%, 100% {
    box-shadow: 0 0 14px 6px rgba(255, 255, 255, .6), 0 0 36px 16px rgba(255, 255, 255, .3);
    box-shadow: 0 0 14px 6px rgba(255, 255, 255, .6), 0 0 36px 16px color-mix(in srgb, var(--client) 35%, white);
  }
  30% {
    box-shadow: 0 0 22px 12px rgba(255, 255, 255, .95), 0 0 70px 34px rgba(255, 255, 255, .45);
    box-shadow: 0 0 22px 12px rgba(255, 255, 255, .95), 0 0 70px 34px color-mix(in srgb, var(--client) 45%, white);
  }
}

.woot-lanceur[data-look="phare"]:hover .woot-phare__disque { filter: brightness(1.08); }
.woot-lanceur[data-look="phare"]:hover .woot-phare__lampe { transform: scale(1.4); }

/* Le cartouche de Phare est ecrit en petites capitales : une inscription, pas une bulle de BD. */
.woot-lanceur[data-look="phare"] .woot-lanceur__cartouche {
  background: var(--woot-surface);
  font-variant: all-small-caps;
  letter-spacing: .07em;
  font-size: 14px;
}

/* OUVERT : le faisceau s'eteint, la lampe se retire, la croix de lumiere se deploie. */
.woot-lanceur[data-look="phare"] .woot-lanceur__croix {
  width: 30px;
  height: 30px;
  margin: -15px;
}

.woot-lanceur[data-look="phare"] .woot-lanceur__croix::before,
.woot-lanceur[data-look="phare"] .woot-lanceur__croix::after {
  box-shadow: 0 0 8px 2px rgba(255, 255, 255, .55);
}

.woot-lanceur[data-look="phare"][data-etat="ouvert"] .woot-phare__faisceau { opacity: 0; }
.woot-lanceur[data-look="phare"][data-etat="ouvert"] .woot-phare__lampe { animation: none; transform: scale(.6); }
`,

  monter: conteneur => {
    const faisceau = document.createElement('span');
    faisceau.className = 'woot-phare__faisceau';

    const disque = document.createElement('div');
    disque.className = 'woot-phare__disque';
    const lentille = document.createElement('span');
    lentille.className = 'woot-phare__lentille';
    disque.appendChild(lentille);

    const lampe = document.createElement('span');
    lampe.className = 'woot-phare__lampe';

    const croix = document.createElement('span');
    croix.className = 'woot-lanceur__croix';

    conteneur.appendChild(faisceau);
    conteneur.appendChild(disque);
    conteneur.appendChild(lampe);
    conteneur.appendChild(croix);
  },

  // PHARE EST DU CSS PUR : les quatre gestes existent parce que le modele les appelle pour tous
  // les looks, et parce que certains — une bouche qui parle en SMIL — en ont besoin. Ici l'attribut
  // d'etat porte tout, et il n'y a rien a mettre en pause que la feuille n'arrete deja.
  gestes: {
    attention: () => {},
    ouvrir: () => {},
    fermer: () => {},
    reduireLeMouvement: () => {},
  },
};
